<template>
  <div
    class="mbti-selection"
    :class="{
      'sidebar-collapsed': isSidebarCollapsed,
      'in-dialog': isInDialog
    }"
  >
    <div v-if="showHeaderAndFooter" class="page-title">请选择你的MBTI</div>
    <!-- MBTI选择网格 -->
    <div class="mbti-grid">
      <div
        v-for="(column, colIndex) in mbtiColumns"
        :key="colIndex"
        class="mbti-column"
        :class="`column-${colIndex + 1}`"
      >
        <div
          v-for="(mbti, index) in column"
          :key="index"
          class="mbti-card"
          :class="{ selected: selectedMbti === mbti.code }"
          @click="selectMbti(mbti.code)"
        >
          <div class="mbti-illustration">
            <img 
              v-if="mbti.image" 
              :src="selectedMbti === mbti.code && mbti.selectedImage ? mbti.selectedImage : mbti.image" 
              :alt="mbti.code" 
              class="mbti-img"
              @error="handleImageError($event, mbti.code)"
            >
            <div v-else class="mbti-placeholder">{{ mbti.code }}</div>
          </div>
        </div>
      </div>
    </div>

    <!-- 底部按钮 -->
    <div v-if="showHeaderAndFooter" class="action-buttons">
      <button class="btn-skip" @click="$emit('skip')">我不想选, 跳过</button>
      <button class="btn-test" @click="$emit('test')">MBTI测试</button>
      <button class="btn-next" @click="$emit('next')" :disabled="!selectedMbti">下一步</button>
    </div>
  </div>
</template>

<script>
import { mbtiTypesList } from '@/utils/mbtiTypes'

export default {
  // 避免与页面组件同名导致递归
  name: 'MbtiSelectionContent',
  props: {
    isSidebarCollapsed: {
      type: Boolean,
      default: false
    },
    // 是否在弹框中展示，用于调整内部 padding
    isInDialog: {
      type: Boolean,
      default: false
    },
    // 是否展示顶部标题和底部按钮，默认 true
    showHeaderAndFooter: {
      type: Boolean,
      default: true
    }
  },
  emits: ['select', 'skip', 'test', 'next'],
  data() {
    return {
      selectedMbti: null,
      mbtiTypes: mbtiTypesList
    }
  },
  computed: {
    // 将数据按列分组（4列）
    mbtiColumns() {
      const columns = [[], [], [], []]
      this.mbtiTypes.forEach((mbti, index) => {
        const colIndex = index % 4
        columns[colIndex].push(mbti)
      })
      return columns
    }
  },
  methods: {
    selectMbti(code) {
      this.selectedMbti = this.selectedMbti === code ? null : code
      // 触发选择事件，仅传递 code 字符串
      this.$emit('select', this.selectedMbti)
    },
    handleImageError(event, code) {
      event.target.style.display = 'none'
      const placeholder = event.target.nextElementSibling || event.target.parentElement.querySelector('.mbti-placeholder')
      if (placeholder) {
        placeholder.textContent = code
        placeholder.style.display = 'flex'
      }
    }
  }
}
</script>

<style lang="scss" scoped>
.mbti-selection {
  display: flex;
  flex-direction: column;
  height: 100%;
  min-height: 0;
}

.page-title {
  font-size: 24px;
  font-weight: 600;
  color: #333;
  margin: 0 0 20px 0;
  text-align: center;
}

.mbti-grid {
  display: flex;
  gap: 20px;
  margin-bottom: 40px;
  flex: 1;
  padding: 0 150px;
  align-items: flex-start; // 改为顶部对齐，避免内容被拉伸
  justify-content: space-evenly;
  min-height: 0; // 允许收缩
  overflow: visible; // 允许内容完整显示，不被裁剪
}

.mbti-column {
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 20px;
  border-radius: 12px;
  padding: 20px;
  min-width: 0; // 允许列收缩
  overflow: visible; // 允许内容完整显示
  
  &.column-1 {
    background: #6D4F601A;
  }

  &.column-2 {
    background: #f2f3f0;
  }

  &.column-3 {
    background: #ebf1f1;
  }

  &.column-4 {
    background: #f6f1e5;
  }
}

.mbti-card {
  cursor: pointer;
  display: flex;
  flex-direction: column;
  flex-shrink: 0; // 卡片不收缩，确保完整显示
  min-height: 0; // 允许内容自适应
  position: relative; // 确保点击区域正确
  user-select: none; // 防止文本选择
}

.mbti-illustration {
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  pointer-events: none; // 防止图片阻止点击事件
}

.mbti-img {
  width: 100%;
  height: 100%;
  object-fit: contain;
}

.mbti-placeholder {
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  background: linear-gradient(135deg, #7EB4EB 0%, #B16EF9 100%);
  color: #ffffff;
  font-size: 18px;
  font-weight: 600;
  border-radius: 8px;
}

.action-buttons {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 40px;
  padding-top: 20px;
  padding: 0 150px;
  flex-shrink: 0; // 按钮区域不收缩
}

// 弹框内的紧凑模式，减少左右留白
.mbti-selection.in-dialog {
  .mbti-grid {
    padding: 0 24px;
    margin-bottom: 0;
  }

  .action-buttons {
    padding: 0 24px;
  }
}

// 按钮通用样式与变体（仅在本组件内复用）
@mixin mbti-btn-base {
  padding: 0 24px;
  height: 40px;
  line-height: 36px;
  border-radius: 8px;
  font-size: 14px;
  cursor: pointer;
  transition: all 0.2s ease;
}

.btn-skip {
  @include mbti-btn-base;
  background: #f5f5f5;
  border: 1px solid #e0e0e0;
  color: #666;
  flex:1;
  &:hover {
    background: #e9ecef;
    border-color: #d0d0d0;
  }
}

.btn-test {
  @include mbti-btn-base;
  background: #ffffff;
  border: 1px solid #7EB4EB;
  flex:1;
  color: #B16EF9;

  &:hover {
    background: #f8f9ff;
    border-color: #B16EF9;
  }
}

.btn-next {
  @include mbti-btn-base;
  background: linear-gradient(90deg, #7EB4EB 0%, #B16EF9 100%);
  border: none;
  flex:1;
  color: #ffffff;
  font-weight: 500;

  &:hover:not(:disabled) {
    opacity: 0.9;
    transform: translateY(-1px);
    box-shadow: 0 4px 8px rgba(177, 110, 249, 0.3);
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
}

// 收缩状态下增加左右padding
.mbti-selection.sidebar-collapsed .mbti-grid,
.mbti-selection.sidebar-collapsed .action-buttons {
  padding: 0 260px;
}
</style>

